import api from './api';

// ============================================
// Interfaces (alle Felder nullable für Robustheit)
// ============================================

export interface ProCountry {
  id?: number;
  name?: string;
}

export interface ProParticipant {
  _id?: string;
  id?: string;
  name?: string;
  shortName?: string;
  slug?: string;
  country?: ProCountry | string;
  image?: string;
}

export interface ProScoreDetail {
  current?: number;
  full?: number;
  display?: string;
  period1?: number;
  period2?: number;
  period3?: number;
}

export interface ProScore {
  home?: ProScoreDetail;
  away?: ProScoreDetail;
}

export interface ProTournamentRef {
  _id?: string;
  flashliveId?: string;
  name?: string;
  ref?: any;
  country?: string;
  category?: string;
  isFeatured?: boolean;
  flashliveTournamentStageId?: string;
}

export interface ProMatchFormat {
  wd?: number;
  bestOf?: number;
}

export interface ProEventStatisticsEntry {
  CATEGORY?: string;
  VALUE_HOME?: string;
  VALUE_AWAY?: string;
}

export interface ProEventStatistics {
  hasStats?: boolean;
  statistics?: ProEventStatisticsEntry[];
}

export interface ProLiveData {
  homeScore?: ProScoreDetail;
  awayScore?: ProScoreDetail;
  homeAverage?: string | number;
  awayAverage?: string | number;
  service?: number;
  stage?: string;
  /** 'LIVE' | 'FINISHED' | 'NOT_STARTED' | 'POSTPONED' — schlägt ProEvent.status */
  stageType?: string | null;
  homeRemainingScore?: number;
  awayRemainingScore?: number;
  /** 'home' | 'away' — wer gerade an der Scheibe steht (aus Throw-by-Throw) */
  currentThrower?: string | null;
  currentThrowerScore?: number | null;
  lastThrow?: string | null;
}

export interface ProEvent {
  _id?: string;
  id?: string;
  flashliveEventId?: string;
  tournament?: ProTournamentRef;
  startTime?: string;
  startUtime?: number;
  status?: string; // 'scheduled' | 'live' | 'finished' | 'postponed' | 'cancelled'
  stageType?: string;
  round?: string;
  homeParticipant?: ProParticipant;
  awayParticipant?: ProParticipant;
  score?: ProScore;
  // Legacy fallback fields (falls altes Backend)
  homeScore?: ProScoreDetail;
  awayScore?: ProScoreDetail;
  winner?: number | null; // 1 = home, 2 = away, null = keiner
  matchFormat?: ProMatchFormat;
  stats?: Record<string, any>;
  statistics?: ProEventStatistics;
  rawData?: Record<string, any>;
  liveData?: ProLiveData;
  lastSyncedAt?: string;
  isActive?: boolean;
  isFeatured?: boolean;
  hasLiveData?: boolean;
}

export interface ProLiveGame extends ProEvent {
  eventId?: string;
  liveData?: ProLiveData;
}

export interface ProEventsResponse {
  status?: string;
  data?: {
    events?: ProEvent[];
    total?: number;
  };
  total?: number;
}

export interface ProLiveGamesCacheResponse {
  status?: string;
  lastUpdated?: string;
  liveGamesCount?: number;
  games?: ProLiveGame[];
  cacheStats?: Record<string, any>;
}

// ============================================
// Service
// ============================================

class ProService {
  // --------------------------------------------
  // Response Extraktion (robust für verschiedene Response-Formate)
  // --------------------------------------------
  private extractEvents(response: any): ProEvent[] {
    if (!response) return [];
    const payload = response.data ?? response;
    if (!payload) return [];

    // Format 1: { status: 'success', data: { events: [...] } }
    if (payload.data?.events && Array.isArray(payload.data.events)) {
      return this.getSafeEventsArray(payload.data.events);
    }
    // Format 2: { events: [...] }
    if (Array.isArray(payload.events)) {
      return this.getSafeEventsArray(payload.events);
    }
    // Format 3: { data: [...] }
    if (Array.isArray(payload.data)) {
      return this.getSafeEventsArray(payload.data);
    }
    // Format 4: direkt [...]
    if (Array.isArray(payload)) {
      return this.getSafeEventsArray(payload);
    }
    return [];
  }

  // --------------------------------------------
  // API Calls
  // --------------------------------------------

  async getLiveEvents(): Promise<ProEvent[]> {
    try {
      const response = await api.get('/api/pro/events/live');
      return this.extractEvents(response);
    } catch (error) {
      console.error('Fehler beim Abrufen der Live-Events:', error);
      return [];
    }
  }

  /**
   * Single-request endpoint mit Live Games + eingebetteten Live-Daten.
   *
   * WICHTIG: Der Backend-Cache markiert im Moment ALLE Events im Cache als `isLive: true`,
   * auch wenn ihr `status` inzwischen 'finished' oder 'scheduled' ist. Daher filtern wir
   * hier mehrstufig:
   *   1. Status muss echt live sein (isLive)
   *   2. stageType darf nicht FINISHED sein
   *   3. Startzeit darf nicht mehr als 4h in der Vergangenheit liegen
   *      (abgebrochene Matches bleiben sonst ewig im Cache)
   *   4. winner muss null/undefined sein (finished Matches haben winner gesetzt)
   */
  async getLiveGamesCache(): Promise<ProLiveGame[]> {
    try {
      const response = await api.get<ProLiveGamesCacheResponse>('/api/pro/events/live-games-cache');
      const payload = response?.data;
      let games: ProLiveGame[] = [];
      if (payload?.games && Array.isArray(payload.games)) {
        games = payload.games.filter(g => g && typeof g === 'object');
      } else {
        games = this.extractEvents(response) as ProLiveGame[];
      }
      return games.filter(g => this.isTrulyLive(g));
    } catch (error) {
      console.error('Fehler beim Abrufen des Live-Games-Cache:', error);
      return [];
    }
  }

  /**
   * Strenge Live-Prüfung – kombiniert Status, stageType, Startzeit und winner.
   * Der Backend-Cache kann "alte" oder bereits beendete Events enthalten.
   */
  private isTrulyLive(event?: ProEvent): boolean {
    if (!event) return false;

    // 1. Status muss echt live sein
    if (!this.isLive(event)) return false;

    // 2. stageType darf nicht FINISHED sein
    const stage = (event.stageType || '').toUpperCase().trim();
    if (stage === 'FINISHED' || stage === 'FINAL' || stage === 'CANCELLED' || stage === 'POSTPONED') {
      return false;
    }

    // 3. winner darf nicht gesetzt sein (1 = home, 2 = away)
    if (event.winner === 1 || event.winner === 2) return false;

    // 4. Startzeit darf nicht mehr als 4h in der Vergangenheit liegen
    const start = this.parseStartTime(event);
    if (start !== null) {
      const ageMs = Date.now() - start;
      const maxAgeMs = 4 * 60 * 60 * 1000; // 4 Stunden
      if (ageMs > maxAgeMs) return false;
      // Startzeit mehr als 10 Minuten in der Zukunft → noch nicht live
      if (ageMs < -10 * 60 * 1000) return false;
    }

    return true;
  }

  private parseStartTime(event?: ProEvent): number | null {
    if (!event) return null;
    if (typeof event.startUtime === 'number' && event.startUtime > 0) {
      // startUtime kann Sekunden oder Millisekunden sein
      const ms = event.startUtime < 1e12 ? event.startUtime * 1000 : event.startUtime;
      return ms;
    }
    if (event.startTime) {
      const t = new Date(event.startTime).getTime();
      if (!isNaN(t)) return t;
    }
    return null;
  }

  async getTodayEvents(): Promise<ProEvent[]> {
    try {
      const response = await api.get('/api/pro/events/today');
      return this.extractEvents(response);
    } catch (error) {
      console.error('Fehler beim Abrufen der heutigen Events:', error);
      return [];
    }
  }

  async getUpcomingEvents(limit: number = 50): Promise<ProEvent[]> {
    try {
      const safeLimit = this.clampLimit(limit, 50);
      const response = await api.get(`/api/pro/events/upcoming?limit=${safeLimit}`);
      return this.extractEvents(response);
    } catch (error) {
      console.error('Fehler beim Abrufen der kommenden Events:', error);
      return [];
    }
  }

  async getFinishedEvents(limit: number = 50): Promise<ProEvent[]> {
    try {
      const safeLimit = this.clampLimit(limit, 50);
      const response = await api.get(`/api/pro/events/finished?limit=${safeLimit}`);
      return this.extractEvents(response);
    } catch (error) {
      console.error('Fehler beim Abrufen der beendeten Events:', error);
      return [];
    }
  }

  /** Primary endpoint für Day-View: Events für Tag inkl. eingebetteter Statistiken. */
  async getEventsByDateWithStats(indentDays: number = 0): Promise<ProEvent[]> {
    try {
      const safeOffset = this.clampOffset(indentDays);
      const response = await api.get(`/api/pro/events/by-date-with-stats?indentDays=${safeOffset}`);
      return this.extractEvents(response);
    } catch (error) {
      console.warn('by-date-with-stats fehlgeschlagen, versuche Fallback auf by-date:', error);
      // Fallback auf alten Endpoint
      return this.getEventsByDate(indentDays);
    }
  }

  /** Events für Tag (indentDays -7..+7). Unterstützt beides: indentDays und date. */
  async getEventsByDate(indentDays: number = 0): Promise<ProEvent[]> {
    try {
      const safeOffset = this.clampOffset(indentDays);

      // Primär mit indentDays versuchen (neues Backend)
      try {
        const response = await api.get(`/api/pro/events/by-date?indentDays=${safeOffset}`);
        const events = this.extractEvents(response);
        if (events.length > 0) return events;
      } catch (_) {
        // Fall-through zum Date-Fallback
      }

      // Fallback: ?date=YYYY-MM-DD (altes Backend)
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() + safeOffset);
      if (isNaN(targetDate.getTime())) return [];
      const dateString = targetDate.toISOString().split('T')[0];
      const response = await api.get(`/api/pro/events/by-date?date=${dateString}`);
      return this.extractEvents(response);
    } catch (error) {
      console.error('Fehler beim Abrufen der Events nach Datum:', error);
      return [];
    }
  }

  /**
   * GET /api/pro/events/:id → { status, data: { …event } }
   * Lädt ein einzelnes Pro-Event (defensiv geparst).
   */
  async getEventById(eventId: string): Promise<ProEvent | null> {
    if (!eventId) return null;
    try {
      const response = await api.get(`/api/pro/events/${encodeURIComponent(eventId)}`);
      const payload: any = response?.data;
      if (!payload) return null;
      // Format 1: { status: 'success', data: {…event} }
      const candidate = payload.data ?? payload;
      if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
        // Verschachtelte Variante { data: { event: {…} } } abfangen
        const event = candidate.event && typeof candidate.event === 'object'
          ? candidate.event
          : candidate;
        if (event && typeof event === 'object') return event as ProEvent;
      }
      return null;
    } catch (error) {
      console.error('Fehler beim Abrufen des Events:', error);
      return null;
    }
  }

  async getEventStatistics(eventId: string): Promise<ProEventStatistics | null> {
    if (!eventId) return null;
    try {
      const response = await api.get(`/api/pro/events/${eventId}/statistics`);
      const payload: any = response?.data;
      if (!payload) return null;
      if (payload.data) return payload.data as ProEventStatistics;
      return payload as ProEventStatistics;
    } catch (error) {
      console.error('Fehler beim Abrufen der Event-Statistiken:', error);
      return null;
    }
  }

  /**
   * GET /api/pro/events/:id/throw-by-throw
   * → { status, data: { throwByThrow: { VALUE, BLOCKS: [...] } | null } }
   * Liefert die rohen FlashLive-BLOCKS (Parsing: throwByThrowParser.ts).
   * ACHTUNG: FlashLive liefert im Leg-Übergang sporadisch LEERE BLOCKS —
   * Aufrufer sollten dann ihren letzten Stand behalten.
   */
  async getEventThrowByThrowBlocks(eventId: string): Promise<any[] | null> {
    if (!eventId) return null;
    try {
      const response = await api.get(
        `/api/pro/events/${encodeURIComponent(eventId)}/throw-by-throw`
      );
      const payload: any = response?.data;
      const blocks = payload?.data?.throwByThrow?.BLOCKS ?? payload?.throwByThrow?.BLOCKS;
      return Array.isArray(blocks) ? blocks : null;
    } catch (error) {
      console.error('Fehler beim Abrufen der Throw-by-Throw-Daten:', error);
      return null;
    }
  }

  async getEventLiveData(eventId: string): Promise<ProLiveData | null> {
    if (!eventId) return null;
    try {
      const response = await api.get(`/api/pro/events/${eventId}/live-data`);
      const payload: any = response?.data;
      if (!payload) return null;
      if (payload.data) return payload.data as ProLiveData;
      return payload as ProLiveData;
    } catch (error) {
      console.error('Fehler beim Abrufen der Live-Daten:', error);
      return null;
    }
  }

  // --------------------------------------------
  // Helper
  // --------------------------------------------

  getEventId(event?: ProEvent): string | undefined {
    if (!event) return undefined;
    return event._id || event.id || event.flashliveEventId;
  }

  /**
   * Der Live-Cache meldet das Spiel als beendet.
   *
   * Hintergrund (Befund 2026-08-22): Nach Matchende hängt `status` 30–90 s auf
   * 'live' — der live-games-cron wartet eine Karenzzeit ab, weil FlashLive das
   * Spiel oft <20 s nach Ende aus der Live-Liste wirft, bevor je ein FINISHED
   * geliefert wurde (live-games-cron.js:808). In diesem Fenster zeigte die
   * Liste einen LIVE-Badge mit veraltetem Stand (real: 5:5 statt 6:5).
   * Der Live-Cache ist sofort korrekt und gewinnt deshalb.
   */
  isFinishedByLiveData(event?: ProEvent): boolean {
    return (event?.liveData?.stageType || '').toUpperCase() === 'FINISHED';
  }

  isLive(event?: ProEvent): boolean {
    // Live-Cache schlägt den (nachhängenden) ProEvent-Status
    if (this.isFinishedByLiveData(event)) return false;
    const s = (event?.status || '').toLowerCase().trim();
    // Strikter Status-Check – das `isLive`-Flag aus dem Backend-Cache ist unzuverlässig.
    return s === 'live' || s === 'inprogress' || s === 'in_progress' || s === 'active';
  }

  isScheduled(event?: ProEvent): boolean {
    const s = (event?.status || '').toLowerCase().trim();
    // Leerer Status zählt NICHT als scheduled – sonst würden unklare Events
    // fälschlich als kommende Spiele angezeigt.
    return s === 'scheduled' || s === 'upcoming' || s === 'planned' || s === 'not_started';
  }

  isFinished(event?: ProEvent): boolean {
    if (this.isFinishedByLiveData(event)) return true;
    const s = (event?.status || '').toLowerCase().trim();
    return s === 'finished' || s === 'ended' || s === 'completed' || s === 'final';
  }

  getHomeScore(event?: ProEvent): number | null {
    // Endstand aus dem Live-Cache ist frischer als das ProEvent-Dokument
    if (this.isFinishedByLiveData(event)) {
      const live = event?.liveData?.homeScore?.current;
      if (typeof live === 'number' && !isNaN(live)) return live;
    }
    const v = event?.score?.home?.current ?? event?.homeScore?.current;
    return typeof v === 'number' && !isNaN(v) ? v : null;
  }

  getAwayScore(event?: ProEvent): number | null {
    if (this.isFinishedByLiveData(event)) {
      const live = event?.liveData?.awayScore?.current;
      if (typeof live === 'number' && !isNaN(live)) return live;
    }
    const v = event?.score?.away?.current ?? event?.awayScore?.current;
    return typeof v === 'number' && !isNaN(v) ? v : null;
  }

  /** Winner als 'home'|'away'|null – bevorzugt explizites winner-Field, fällt sonst auf Scorevergleich zurück. */
  getEventWinner(event?: ProEvent): 'home' | 'away' | null {
    if (!event) return null;
    if (event.winner === 1) return 'home';
    if (event.winner === 2) return 'away';
    if (!this.isFinished(event)) return null;
    const h = this.getHomeScore(event);
    const a = this.getAwayScore(event);
    if (h === null || a === null) return null;
    if (h > a) return 'home';
    if (a > h) return 'away';
    return null;
  }

  formatScore(event?: ProEvent): { home: string; away: string; } {
    if (!event) return { home: '-', away: '-' };
    if (this.isFinished(event) || this.isLive(event)) {
      const h = this.getHomeScore(event);
      const a = this.getAwayScore(event);
      return {
        home: h !== null ? h.toString() : '0',
        away: a !== null ? a.toString() : '0'
      };
    }
    return { home: '-', away: '-' };
  }

  getStatusBadge(status?: string): { text: string; color: string; animate?: boolean } {
    const s = (status || '').toLowerCase().trim();
    if (s === 'live' || s === 'inprogress' || s === 'active') {
      return { text: 'LIVE', color: 'danger', animate: true };
    }
    if (s === 'finished' || s === 'ended' || s === 'completed') {
      return { text: 'Beendet', color: 'default' };
    }
    if (s === 'postponed') return { text: 'Verschoben', color: 'warning' };
    if (s === 'cancelled') return { text: 'Abgesagt', color: 'default' };
    return { text: 'Geplant', color: 'primary' };
  }

  formatEventTime(startTime?: string): string {
    if (!startTime) return '-';
    try {
      const date = new Date(startTime);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '-';
    }
  }

  formatEventDate(startTime?: string): string {
    if (!startTime) return '-';
    try {
      const date = new Date(startTime);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return '-';
    }
  }

  cleanPlayerName(name?: string): string {
    if (!name) return 'Unbekannt';
    return name.replace(/\s*\([^)]*\)\s*$/, '').trim() || 'Unbekannt';
  }

  getParticipantName(participant?: ProParticipant): string {
    if (!participant) return 'Unbekannt';
    const raw = participant.name || participant.shortName;
    return this.cleanPlayerName(raw);
  }

  getParticipantInitials(participant?: ProParticipant): string {
    const name = this.getParticipantName(participant);
    if (name === 'Unbekannt') return '?';
    const parts = name.split(' ').filter(p => p.trim());
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  getParticipantImage(participant?: ProParticipant): string | null {
    if (!participant) return null;
    const img = participant.image;
    if (typeof img === 'string' && img.trim()) return img.trim();
    return null;
  }

  getTournamentName(tournament?: ProTournamentRef): string {
    if (!tournament) return 'Unbekanntes Turnier';
    const name = tournament.name;
    return typeof name === 'string' && name.trim() ? name.trim() : 'Unbekanntes Turnier';
  }

  /** Extrahiert Averages aus embedded statistics */
  getAverages(event?: ProEvent): { home: string; away: string } | null {
    const stats = event?.statistics;
    if (!stats || stats.hasStats !== true) return null;
    const list = stats.statistics;
    if (!Array.isArray(list) || list.length === 0) return null;
    for (const entry of list) {
      const category = entry?.CATEGORY || '';
      if (category.includes('Durchschnitt') || category.includes('Average')) {
        const h = entry?.VALUE_HOME;
        const a = entry?.VALUE_AWAY;
        const hasHome = h && h !== '0.00' && h !== '';
        const hasAway = a && a !== '0.00' && a !== '';
        if (hasHome || hasAway) {
          return {
            home: hasHome ? String(h) : '-',
            away: hasAway ? String(a) : '-',
          };
        }
      }
    }
    return null;
  }

  /**
   * Restpunkte beider Spieler bei laufenden Spielen.
   *
   * Quelle: liveData aus dem LiveGameCache (seit 2026-08-22 additiv in
   * /api/pro/events/by-date-with-stats). Liefert nur etwas, wenn BEIDE Werte
   * vorliegen — eine halbe Anzeige wäre irreführend. Bei altem Backend bzw.
   * ohne Throw-by-Throw-Daten: null.
   */
  getLiveRemaining(event?: ProEvent): { home: number; away: number } | null {
    if (!this.isLive(event)) return null;
    const home = event?.liveData?.homeRemainingScore;
    const away = event?.liveData?.awayRemainingScore;
    if (typeof home !== 'number' || typeof away !== 'number') return null;
    if (!Number.isFinite(home) || !Number.isFinite(away)) return null;
    return { home, away };
  }

  /**
   * Wer steht gerade an der Scheibe? 'home' | 'away' | null (unbekannt).
   * Nur bei laufenden Spielen.
   */
  getCurrentThrower(event?: ProEvent): 'home' | 'away' | null {
    if (!this.isLive(event)) return null;
    const thrower = event?.liveData?.currentThrower;
    if (typeof thrower !== 'string') return null;
    const normalized = thrower.trim().toLowerCase();
    return normalized === 'home' || normalized === 'away' ? normalized : null;
  }

  /** Super Series/ADC gelten im Ticker als nachrangig gegenüber PDC & Co. */
  isLowPriorityTournament(event?: ProEvent): boolean {
    const name = (event?.tournament?.name || '').toLowerCase();
    return name.includes('super series') || name.includes('adc');
  }

  /**
   * Primär PDC & Co. vor Super Series/ADC, sekundär nach Startzeit
   * (`newestFirst` kehrt die Zeitrichtung um — für beendete Spiele).
   */
  compareTickerPriority(a: ProEvent, b: ProEvent, newestFirst = false): number {
    const aLow = this.isLowPriorityTournament(a);
    const bLow = this.isLowPriorityTournament(b);
    if (aLow && !bLow) return 1;
    if (!aLow && bLow) return -1;
    const ta = a.startTime ? new Date(a.startTime).getTime() : 0;
    const tb = b.startTime ? new Date(b.startTime).getTime() : 0;
    return newestFirst ? tb - ta : ta - tb;
  }

  /**
   * Ticker-Reihenfolge (identisch zur App, `orderTickerEvents`):
   * anstehende Spiele (Zeit AUFSTEIGEND — das nächste zuerst) vor beendeten
   * (Zeit ABSTEIGEND — das zuletzt beendete zuerst); innerhalb jeder Gruppe
   * PDC & Co. vor Super Series/ADC. Events mit unklarem Status ans Ende.
   *
   * Bewusst getrennt von `filterAndSortByDay`: Die Tagesliste auf /spiele
   * bleibt chronologisch aufsteigend, nur der Ticker gruppiert nach Status.
   */
  sortForTicker(events: ProEvent[]): ProEvent[] {
    const all = this.getSafeEventsArray(events);
    const scheduled = all
      .filter((e) => this.isScheduled(e))
      .sort((a, b) => this.compareTickerPriority(a, b));
    const finished = all
      .filter((e) => this.isFinished(e))
      .sort((a, b) => this.compareTickerPriority(a, b, true));
    const unknown = all.filter((e) => !this.isScheduled(e) && !this.isFinished(e));
    return [...scheduled, ...finished, ...unknown];
  }

  /** Gruppiert Events nach Turnier-Namen (preserviert Reihenfolge). */
  groupByTournament(events: ProEvent[]): Map<string, ProEvent[]> {
    const map = new Map<string, ProEvent[]>();
    for (const event of events) {
      const name = this.getTournamentName(event?.tournament);
      if (!map.has(name)) map.set(name, []);
      map.get(name)!.push(event);
    }
    return map;
  }

  /**
   * Filter + Sort analog Flutter: Live-Events raus, nur Events am Ziel-Tag,
   * "super series" und "adc" Turniere an das Ende.
   */
  filterAndSortByDay(events: ProEvent[], indentDays: number): ProEvent[] {
    const safeEvents = this.getSafeEventsArray(events);
    const nonLive = safeEvents.filter(e => !this.isLive(e));

    const now = new Date();
    const target = new Date(now);
    target.setDate(target.getDate() + indentDays);
    const dayStart = new Date(target.getFullYear(), target.getMonth(), target.getDate(), 0, 0, 0);
    const dayEnd = new Date(target.getFullYear(), target.getMonth(), target.getDate(), 23, 59, 59);

    const onDay = nonLive.filter(event => {
      if (!event.startTime) return false;
      const t = new Date(event.startTime);
      if (isNaN(t.getTime())) return false;
      return t >= dayStart && t <= dayEnd;
    });

    onDay.sort((a, b) => {
      const aName = (a.tournament?.name || '').toLowerCase();
      const bName = (b.tournament?.name || '').toLowerCase();
      const aLow = aName.includes('super series') || aName.includes('adc');
      const bLow = bName.includes('super series') || bName.includes('adc');
      if (aLow && !bLow) return 1;
      if (!aLow && bLow) return -1;
      const ta = a.startTime ? new Date(a.startTime).getTime() : 0;
      const tb = b.startTime ? new Date(b.startTime).getTime() : 0;
      return ta - tb;
    });

    return onDay;
  }

  getSafeEventsArray(data?: ProEvent[] | null): ProEvent[] {
    if (!data || !Array.isArray(data)) return [];
    return data.filter(e => e && typeof e === 'object');
  }

  // --------------------------------------------
  // Private Utils
  // --------------------------------------------
  private clampLimit(v: number, def: number): number {
    if (typeof v !== 'number' || isNaN(v) || v <= 0) return def;
    return Math.min(Math.round(v), 1000);
  }

  private clampOffset(v: number): number {
    if (typeof v !== 'number' || isNaN(v)) return 0;
    return Math.max(-365, Math.min(365, Math.round(v)));
  }
}

export default new ProService();
